import Skeleton from 'react-loading-skeleton';
import styled from 'styled-components';

import { Tag } from '../../../cdl/Tag/Tag';
import { Text } from '../../../cdl/Text/Text';
import { Tooltip } from '../../../cdl/Tooltip/Tooltip';
import { translate } from '../../../common/helpers/translate.helper';
import { IconInfoCircle } from '../../../common/icons/cdl/InfoCircle';
import { InfoPopover } from '../../../common/InfoPopover/InfoPopover';
import { Box } from '../../../common/ui/Box';
import { theme } from '../../../common/ui/theme';
import { OutdatedPriceTag } from '../../create-enquiry/components/OutdatedPriceTag';
import {
    SupplierSelectionPriceState,
    SupplierSelectionRowData,
} from '../../create-enquiry/components/SupplierSelection.types';
import { SupplierSelectionIndicator } from '../../create-enquiry/components/SupplierSelectionIndicator';
import { SupplierSelectionRowContainer } from '../../create-enquiry/components/SupplierSelectionRowContainer';

const MetaList = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    column-gap: ${theme.space[8]};
    row-gap: ${theme.space[4]};
`;

interface SupplierSelectionRowProps {
    row: SupplierSelectionRowData;
}

export const SupplierSelectionRow = ({ row }: SupplierSelectionRowProps) => {
    return (
        <SupplierSelectionRowContainer
            $disabled={row.disabled}
            $hasMultipleContracts={row.hasMultipleContracts}
            $selected={row.selected}
            onClick={row.disabled ? undefined : row.onToggle}
            onKeyDown={(event) => {
                if (row.disabled) {
                    return;
                }

                if (event.key !== 'Enter' && event.key !== ' ') {
                    return;
                }

                event.preventDefault();
                row.onToggle();
            }}
            role="checkbox"
            aria-checked={row.selected}
            aria-disabled={row.disabled}
            tabIndex={row.disabled ? -1 : 0}
            data-testid={`supplier-selection-row-${row.id}`}
        >
            <Box display="flex" alignItems="center" gap={12} minWidth={0} flex="1">
                {row.leadingVisual}

                <Box minWidth={0} flex="1">
                    <Box display="flex" alignItems="center" gap={8} minWidth={0}>
                        <Text
                            variant="subtitle1"
                            color="foreground.default"
                            style={{
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}
                        >
                            {row.title}
                        </Text>

                        {row.limitedTooltip ? (
                            <Tooltip label={row.limitedTooltip} showArrow>
                                <Tag variant="attention" Icon={IconInfoCircle}>
                                    {translate('order.supplierSelection.limited')}
                                </Tag>
                            </Tooltip>
                        ) : null}
                    </Box>

                    <MetaList>
                        {row.meta.map((meta) => {
                            return (
                                <Text
                                    key={meta}
                                    variant="extraSmall"
                                    color="foreground.muted"
                                    fontWeight={meta === translate('labelList.default') ? 600 : undefined}
                                >
                                    {meta}
                                </Text>
                            );
                        })}
                    </MetaList>
                </Box>
            </Box>

            <Box display="flex" alignItems="center" gap={16} flexShrink={0} paddingRight={4}>
                {row.price?.state === SupplierSelectionPriceState.LOADING ? <Skeleton width={96} height={24} /> : null}

                {row.price?.state === SupplierSelectionPriceState.AVAILABLE ||
                row.price?.state === SupplierSelectionPriceState.UNAVAILABLE ? (
                    <Box display="flex" alignItems="center" gap={8}>
                        {row.price.isOutdated ? <OutdatedPriceTag validUntil={row.price.validUntil} /> : null}

                        {row.price.popoverContent && !row.disabled ? (
                            <InfoPopover side="left" align="start">
                                {row.price.popoverContent}
                            </InfoPopover>
                        ) : null}

                        <Text variant="subtitle1" color="foreground.default">
                            {row.price.label}
                        </Text>
                    </Box>
                ) : null}

                <SupplierSelectionIndicator selected={row.selected} disabled={row.disabled} />
            </Box>
        </SupplierSelectionRowContainer>
    );
};
